/**
 * The console shell: the same collapsible HeroUI Pro sidebar the AI
 * Workbench uses, with the console's views grouped the way an administrator
 * thinks about them: Home (the setup checklist, then a summary), Work (projects and what runs on them), Fleet (the
 * machines and what they run), Rules (what they may do and know), Money and
 * Record. The person sits at the foot: an account menu with appearance and
 * Sign out.
 *
 * Mojo Up AI Cloud shows the organisation switcher in the header, a Team view
 * under Work, and no link to what Cloud does not offer (Portfolio,
 * Budgets, Costs; Mesh unless the discovery document offers it).
 */

import { ArrowDownToLine, ArrowsRotateRight, Bell, Briefcase, ChartColumn, ClockArrowRotateLeft, Cpu, CreditCard, SealCheck, Database, Folder, Display, Gear, HeartPulse, House, SquarePlus, Key, ListCheck, NodesRight, Palette, Persons, Pulse, Puzzle, Shield, ShieldCheck, ShieldKeyhole, Sliders, Tag, Wrench } from '@gravity-ui/icons';
import { CircleQuestion, Cloud, LayoutCellsLarge, Server, Speedometer } from '@gravity-ui/icons';
import type { AreaKey, MyPermissions } from '@aioe/shared';
import { Sidebar } from '@heroui-pro/react';
import { useEffect, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { operatorAccessRoutes, routes } from '../../api/client.ts';
import { useLoad } from '../../hooks.ts';
import { sees, usePermissions } from '../../permissions.ts';
import { useAuth } from '../../auth/AuthProvider.tsx';
import { useCloudStanding, type CloudStanding } from '../../cloudPlan.ts';
import { isCloud, productByline, productShortName } from '../../edition.ts';
import { loadConsoleDesignSystem } from '../../orgDesignSystem.ts';
import { DesignSystemTryOn } from '../DesignSystemTryOn.tsx';
import { OrganisationSwitcher } from '../cloud/OrganisationSwitcher.tsx';
import { TEAM_ATTENTION_CHANGED } from '../teams/teamsModel.ts';
import { AccountMenu } from './AccountMenu.tsx';

interface NavView {
  path: string;
  label: string;
  subtitle: string;
  icon: ReactNode;
  /** Shown only on Enterprise (Cloud does not offer it), or only on Cloud. */
  edition?: 'enterprise' | 'cloud';
  /** On Cloud, shown only when the discovery document offers this capability (the mesh, with AIOE_MESH_CLOUD=on). */
  cloudCapability?: string;
  /** Shown only to the platform's operators (Mojo Up's own administrators on Mojo Up AI Cloud). */
  operatorOnly?: boolean;
  /** On Cloud, shown only to its operators (the service's own status page); every Enterprise administrator sees it. */
  cloudOperatorOnly?: boolean;
  /**
   * On Cloud without Teams (cloudPlan.ts): `account` views are every organisation's, `team` views a team's on any
   * plan; the rest are shown only once the organisation has Teams.
   */
  cloudPlace?: 'account' | 'team';
  /** Its name on Cloud, where "Teams" is the plan. */
  cloudLabel?: string;
  /**
   * The areas it shows (docs/rbac.md section 3). With roles and permissions on, it is shown only to someone who may View
   * one of them; while they are off, as always, to everyone.
   */
  areas?: AreaKey[];
}

const NAV_GROUPS: { label: string; views: NavView[] }[] = [
  {
    label: '',
    views: [{ path: '/', cloudPlace: 'account', label: 'Home', subtitle: 'Setting up the organisation, then what needs you and what is running', icon: <House /> }],
  },
  {
    label: 'Work',
    views: [
      { path: '/projects', areas: ['projects.projects', 'projects.register'], label: 'Projects', subtitle: 'Projects, their members, repositories and compute', icon: <Folder /> },
      { path: '/teams', areas: ['teams.teams', 'agents.specialists'], cloudLabel: 'Agent teams', label: 'Teams', subtitle: 'Every team and specialist, where it was defined, and which machines run it', icon: <Persons /> },
      { path: '/portfolio', areas: ['teams.portfolio'], label: 'Portfolio', subtitle: 'Business areas, their projects, and filing requests waiting on you', icon: <Briefcase /> },
      { path: '/activity', areas: ['agents.activity', 'agents.live'], label: 'Activity', subtitle: 'Every running agent across the fleet, and the events they report', icon: <Pulse /> },
      { path: '/team', areas: ['roles.people'], cloudPlace: 'team', edition: 'cloud', label: 'People', subtitle: 'The people in this organisation, their roles, and invitations', icon: <Persons /> },
      { path: '/billing', areas: ['economics.billing'], cloudPlace: 'account', edition: 'cloud', label: 'Billing', subtitle: 'The plan, what it uses of it, invoices and add-ons', icon: <CreditCard /> },
      { path: '/help', cloudPlace: 'account', edition: 'cloud', label: 'Help and support', subtitle: "The service's health, and your support requests: help, bugs and billing", icon: <CircleQuestion /> },
    ],
  },
  {
    label: 'Fleet',
    views: [
      { path: '/workbenches', areas: ['machines.workbenches'], cloudPlace: 'account', label: 'Workbenches', subtitle: 'Every enrolled AI Workbench, who owns it and whether it is online', icon: <Display /> },
      { path: '/nodes', areas: ['machines.nodes'], cloudPlace: 'account', label: 'Nodes', subtitle: 'Headless workbenches for shared compute, and the approvals they wait on', icon: <Cpu /> },
      { path: '/updates', areas: ['machines.updates'], label: 'Updates', subtitle: 'What the fleet runs, and which ring gets each release next', icon: <ArrowsRotateRight /> },
      { path: '/backups', areas: ['machines.backups'], label: 'Backups', subtitle: 'Workbench backups, encrypted on the machine before they leave it', icon: <ClockArrowRotateLeft /> },
      { path: '/downloads', cloudPlace: 'account', label: 'Downloads', subtitle: 'Installers for AI Workbench and nodes', icon: <ArrowDownToLine /> },
      { path: '/mesh', areas: ['machines.mesh', 'machines.mesh-settings'], edition: 'enterprise', label: 'Mesh', subtitle: 'The private network between machines: addresses and last contact', icon: <NodesRight /> },
      { path: '/enrol', areas: ['machines.workbenches'], cloudPlace: 'account', label: 'Add a workbench', subtitle: 'Approve the code an AI Workbench shows when it connects', icon: <SquarePlus /> },
    ],
  },
  {
    label: 'Rules',
    views: [
      { path: '/policies', areas: ['rules.policies', 'rules.exceptions'], label: 'Policies', subtitle: 'The rules workbenches follow: models, data, approvals and remote control', icon: <Shield /> },
      { path: '/assurance', areas: ['rules.providers'], label: 'Providers and Models', subtitle: 'How far each AI provider can be trusted with data, and the organisation’s own ratings', icon: <ShieldCheck /> },
      { path: '/tools', areas: ['rules.tools'], label: 'Tools', subtitle: 'The tool servers agents may use, and what each tool may do', icon: <Wrench /> },
      { path: '/catalogue', areas: ['rules.catalogue'], label: 'Catalogue', subtitle: 'Teams, roles, skills and prompts workbenches pull into their templates', icon: <ListCheck /> },
      { path: '/customisations', areas: ['rules.customisations'], label: 'Customisations', subtitle: "Agents, instructions, hooks and plugins for every workbench, people's submissions, and what each machine has", icon: <Puzzle /> },
      { path: '/apps-and-routines', areas: ['workflows.apps', 'workflows.routines', 'workflows.workflows', 'workflows.event-sources'], label: 'Apps and routines', subtitle: 'What people share from their workbench for a project, its versions, what waits for review, and how workflows ran', icon: <LayoutCellsLarge /> },
      { path: '/foundry', areas: ['rules.policies', 'agents.specialists'], label: 'Foundry', subtitle: 'The Microsoft Foundry projects people may use, their requests to add more, and the agents they publish', icon: <Cloud /> },
      { path: '/compute', areas: ['machines.nodes'], edition: 'enterprise', label: 'Compute', subtitle: 'The clusters work may run on, their node pools and which projects they serve, and requests to add more', icon: <Server /> },
      { path: '/design-systems', areas: ['rules.design-systems'], label: 'Design systems', subtitle: "The organisation's look, installed as a skill on every workbench so what agents make looks like yours", icon: <Palette /> },
      { path: '/secrets', areas: ['data.secrets'], label: 'Secrets', subtitle: "API keys and tokens agents' tools need, released only to signed-in machines", icon: <Key /> },
      { path: '/quotas', areas: ['data.quotas'], edition: 'enterprise', label: 'Quotas', subtitle: 'How much the organisation, a person or a project may keep, and what is used of it', icon: <Sliders /> },
      { path: '/memory', areas: ['data.memory'], label: 'Memory', subtitle: 'What the organisation knows, and who may read it', icon: <Database /> },
    ],
  },
  {
    label: 'Money',
    views: [
      { path: '/costs', areas: ['economics.costs', 'economics.chargeback'], label: 'Costs', subtitle: 'What the work cost, by team, agent, session, project and repository', icon: <ChartColumn /> },
      { path: '/budgets', areas: ['economics.budgets'], label: 'Budgets', subtitle: 'Spend limits, and where each one stands', icon: <CreditCard /> },
    ],
  },
  {
    label: 'Record',
    views: [
      { path: '/audit', areas: ['audit.audit'], label: 'Audit', subtitle: 'What people, agents and machines did, kept so it cannot be changed unseen', icon: <SealCheck /> },
      { path: '/labelling', areas: ['organisation.integrations'], label: 'Labelling', subtitle: 'How much of the organisation’s content carries a sensitivity label, across memory, sessions, projects and portfolios', icon: <Tag /> },
      { path: '/platform-access', areas: ['audit.platform-access'], edition: 'enterprise', label: 'Platform access', subtitle: "Who at the platform's operator read the organisation's data, when and why, and what its own jobs read", icon: <ShieldKeyhole /> },
      { path: '/status', areas: ['organisation.status'], cloudOperatorOnly: true, label: 'Status', subtitle: "The platform's public status page, and the incidents written for it", icon: <HeartPulse /> },
      { path: '/operate', edition: 'cloud', operatorOnly: true, label: 'Operate', subtitle: 'Mojo Up AI Cloud as a whole: health, load, storage, every organisation, and removals', icon: <Speedometer /> },
    ],
  },
];

/** The groups this edition offers; a group left with nothing is not shown. */
function navGroups(capabilities: readonly string[], operator: boolean, standing: CloudStanding, permissions: MyPermissions | undefined) {
  const edition = isCloud() ? 'cloud' : 'enterprise';
  const offered = (v: NavView) => edition !== 'cloud' || !v.cloudCapability || capabilities.includes(v.cloudCapability);
  const placed = (v: NavView) => edition !== 'cloud' || standing.teams || v.cloudPlace === 'account' || (v.cloudPlace === 'team' && !standing.personal);
  // Roles on: only the areas the person may View (the API refuses the rest anyway). Off, or not yet read: as always.
  // The platform's operators' own views on Cloud are theirs by operator standing, never a role in the organisation.
  const permitted = (v: NavView) => permissions?.mode !== 'roles' || !v.areas || (edition === 'cloud' && v.cloudOperatorOnly) || v.areas.some((a) => sees(permissions, a));
  const shown = (v: NavView) => (!v.edition || v.edition === edition) && offered(v) && placed(v) && permitted(v) && (!v.operatorOnly || operator) && (edition !== 'cloud' || !v.cloudOperatorOnly || operator);
  const groups = NAV_GROUPS.map((g) => ({ ...g, views: g.views.filter(shown).map((v) => (edition === 'cloud' && v.cloudLabel ? { ...v, label: v.cloudLabel } : v)) })).filter((g) => g.views.length > 0);
  if (edition !== 'cloud' || standing.teams) return groups;
  // An account, not a workspace: its machines first, then the account itself; operators keep their own group.
  const all = groups.flatMap((g) => g.views);
  const pick = (paths: string[]) => paths.flatMap((p) => all.filter((v) => v.path === p));
  return [
    { label: '', views: pick(['/', '/workbenches', '/nodes', '/enrol', '/downloads']) },
    { label: 'Account', views: pick(['/team', '/billing', '/help']) },
    { label: 'Operator', views: all.filter((v) => v.operatorOnly || v.cloudOperatorOnly) },
  ].filter((g) => g.views.length > 0);
}

/** A count beside a view's name: what waits there (the teams with something to fix). Never shown as zero. */
function NavItem({ view, current, onAction, count }: { view: NavView; current: boolean; onAction: () => void; count?: { n: number; label: string } }) {
  return (
    <Sidebar.MenuItem
      id={`nav-${view.path}`}
      textValue={view.label}
      isCurrent={current}
      onAction={onAction}
      tooltip={`${view.label} — ${view.subtitle}`}
      tooltipProps={{ content: view.subtitle, delay: 600, placement: 'right', className: 'max-w-64 text-2xs' }}
    >
      <Sidebar.MenuItemContent>
        <Sidebar.MenuIcon>{view.icon}</Sidebar.MenuIcon>
        <Sidebar.MenuLabel>{view.label}</Sidebar.MenuLabel>
        {count && count.n > 0 ? (
          <Sidebar.MenuChip aria-label={count.label} data-nav-count={view.path} className="tabular-nums">
            {count.n > 99 ? '99+' : count.n}
          </Sidebar.MenuChip>
        ) : null}
      </Sidebar.MenuItemContent>
    </Sidebar.MenuItem>
  );
}

function ShellSidebarContent() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { discovery } = useAuth();
  // Whether this person is one of the platform's operators (Cloud): the same answer the Platform access page reads.
  const grants = useLoad(() => (isCloud() ? operatorAccessRoutes.grants().catch(() => undefined) : Promise.resolve(undefined)), []);
  const operator = grants.data?.mayOpen ?? false;
  const standing = useCloudStanding();
  const permissions = usePermissions();
  const groups = navGroups(discovery?.capabilities ?? [], operator, standing, permissions.data);
  // Teams with something to fix (Check this team): errors only, warnings wait on the page. Stored data, cheap; every minute.
  const showsTeams = groups.some((g) => g.views.some((v) => v.path === '/teams'));
  const attention = useLoad(() => (showsTeams ? routes.teamAttention().catch(() => undefined) : Promise.resolve(undefined)), [showsTeams], 60_000);
  const reloadAttention = attention.reload;
  useEffect(() => {
    window.addEventListener(TEAM_ATTENTION_CHANGED, reloadAttention);
    return () => window.removeEventListener(TEAM_ATTENTION_CHANGED, reloadAttention);
  }, [reloadAttention]);
  const broken = (attention.data?.teams ?? []).filter((t) => t.errors > 0).length;
  const countOf = (path: string) => (path === '/teams' && broken ? { n: broken, label: `${broken} ${broken === 1 ? 'team needs' : 'teams need'} fixing` } : undefined);
  return (
    <>
      <Sidebar.Header>
        {isCloud() ? (
          <div className="px-1 pt-1">
            <OrganisationSwitcher />
          </div>
        ) : (
          <div className="flex items-center gap-2 px-2 pt-2">
            <img src="/mojoup-logo.svg" alt="Mojo Up" className="size-7 shrink-0 rounded-lg object-contain" draggable={false} />
            <div className="sidebar-collapse-hide min-w-0">
              <div className="truncate text-body font-semibold">{productShortName()}</div>
              <div className="truncate text-2xs text-muted">{productByline(discovery?.organisation)}</div>
            </div>
          </div>
        )}
      </Sidebar.Header>
      <Sidebar.Content>
        {groups.map((group) => (
          <Sidebar.Group key={group.label}>
            {group.label ? <Sidebar.GroupLabel>{group.label}</Sidebar.GroupLabel> : null}
            <Sidebar.Menu aria-label={group.label || 'Home'}>
              <Sidebar.MenuSection>
                {group.views.map((v) => (
                  <NavItem key={v.path} view={v} current={v.path === '/' ? pathname === '/' : pathname.startsWith(v.path)} onAction={() => navigate(v.path === '/teams' && broken ? '/teams?filter=attention' : v.path)} count={countOf(v.path)} />
                ))}
              </Sidebar.MenuSection>
            </Sidebar.Menu>
          </Sidebar.Group>
        ))}
      </Sidebar.Content>
      <Sidebar.Footer>
        <Sidebar.Menu aria-label="Settings">
          <Sidebar.MenuSection>
            <NavItem
              view={{ path: '/settings', label: 'Settings', subtitle: 'Your own settings, and the organisation’s if you look after it', icon: <Gear /> }}
              current={pathname.startsWith('/settings')}
              onAction={() => navigate('/settings')}
            />
          </Sidebar.MenuSection>
        </Sidebar.Menu>
        <div className="px-1 pb-1">
          <AccountMenu />
        </div>
      </Sidebar.Footer>
    </>
  );
}

/** The bell: how many unread records wait across the person's projects; opens the Inbox on exactly those. */
function InboxBell() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const inbox = useLoad(() => routes.inbox('unread', 1), [], 30_000);
  const unread = inbox.data?.unread ?? 0;
  const label = unread ? `Inbox, ${unread} unread: show them` : 'Inbox';
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-current={pathname.startsWith('/inbox') ? 'page' : undefined}
      onClick={() => navigate(unread ? '/inbox?show=unread' : '/inbox')}
      className="relative inline-flex size-8 items-center justify-center rounded-lg text-muted hover:bg-surface-secondary hover:text-foreground aria-[current=page]:text-foreground"
    >
      <Bell className="size-4" />
      {unread > 0 && (
        <span className="absolute -right-0.5 -top-0.5 min-w-4 rounded-full bg-warning px-1 text-center text-2xs font-semibold leading-4 text-warning-foreground tabular-nums" aria-hidden="true">
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </button>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  // The organisation's design system, when an administrator has the console wear it.
  useEffect(() => {
    void loadConsoleDesignSystem();
  }, []);
  return (
    <Sidebar.Provider defaultOpen collapsible="icon">
      <Sidebar>
        <ShellSidebarContent />
        <Sidebar.Rail />
      </Sidebar>
      <Sidebar.Mobile>
        <ShellSidebarContent />
      </Sidebar.Mobile>
      <Sidebar.Main className="relative flex h-full min-w-0 flex-1 flex-col overflow-hidden bg-background">
        <header className="flex h-11 shrink-0 items-center gap-2 border-b border-border px-3">
          <Sidebar.Trigger aria-label="Toggle sidebar" />
          <div className="flex-1" />
          <InboxBell />
        </header>
        <DesignSystemTryOn />
        <main className="app-content min-h-0 flex-1 overflow-auto p-4 md:p-6">{children}</main>
      </Sidebar.Main>
    </Sidebar.Provider>
  );
}
